import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

const messages = {
  ru: {
    product: 'Unified Agentic Workspace',
    tagline: 'Координация людей и локальных ИИ‑агентов',
    workspaces: 'Пространства',
    sessions: 'Агенты',
    workItems: 'Работы',
    overlaps: 'Пересечения',
    activity: 'Активность',
    repositories: 'Репозитории',
    settings: 'Подключение',
    logout: 'Выйти',
    loading: 'Загрузка…',
    empty: 'Пока пусто',
    create: 'Создать',
    back: 'Назад',
    online: 'В сети',
    noIntent: 'Намерение не опубликовано',
    open: 'Открыто',
    details: 'Подробнее'
  },
  en: {
    product: 'Unified Agentic Workspace',
    tagline: 'Coordination for people and local AI agents',
    workspaces: 'Workspaces',
    sessions: 'Agents',
    workItems: 'Work',
    overlaps: 'Overlaps',
    activity: 'Activity',
    repositories: 'Repositories',
    settings: 'Connect',
    logout: 'Sign out',
    loading: 'Loading…',
    empty: 'Nothing here yet',
    create: 'Create',
    back: 'Back',
    online: 'Online',
    noIntent: 'Intent not published',
    open: 'Open',
    details: 'Details'
  }
} as const;

type Locale = keyof typeof messages;
type MessageKey = keyof (typeof messages)['ru'];

const I18nContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
} | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    localStorage.getItem('uaw-locale') === 'en' ? 'en' : 'ru'
  );
  const value = useMemo(
    () => ({
      locale,
      setLocale: (next: Locale) => {
        localStorage.setItem('uaw-locale', next);
        document.documentElement.lang = next;
        setLocaleState(next);
      },
      t: (key: MessageKey) => messages[locale][key]
    }),
    [locale]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error('I18nProvider is missing');
  return value;
}
